/**
 * SHOPUS-241: set link targets once when the page (or new markup) renders.
 * Internal storefront links stay in the same tab; external links open in a new tab.
 * Clicks are not intercepted — the browser follows the updated target attribute.
 */

const SKIP_PROTOCOL = /^(mailto|tel|sms|javascript):/i;

/** Apex + www storefront hosts (not helpcenter / help). */
const STOREFRONT_HOST_RE = /^(www\.)?paulaschoice\.(com|com\.au|co\.uk)$/i;

/** Shopify preview / test storefront hosts. */
const SHOPIFY_PREVIEW_HOST_RE =
  /^shopify(test|prod|dev)\.paulaschoice\.(com|com\.au|co\.uk)$/i;

/**
 * @returns {Set<string>}
 */
function storefrontHosts() {
  const hosts = new Set();
  const extra = window.__pcLinkTargets?.hosts ?? [];
  for (const host of extra) {
    if (typeof host === "string" && host) hosts.add(host.toLowerCase());
  }
  hosts.add(window.location.hostname.toLowerCase());
  return hosts;
}

const knownStorefrontHosts = storefrontHosts();

/**
 * @param {string} hostname
 * @returns {boolean}
 */
function isInternalHost(hostname) {
  if (!hostname) return true;
  const host = hostname.toLowerCase();
  if (knownStorefrontHosts.has(host)) return true;
  return STOREFRONT_HOST_RE.test(host) || SHOPIFY_PREVIEW_HOST_RE.test(host);
}

/**
 * @param {string} href
 * @returns {boolean}
 */
function shouldSkipHref(href) {
  const value = href.trim();
  if (!value || value === "#") return true;
  return SKIP_PROTOCOL.test(value);
}

/**
 * @param {string} href
 * @returns {boolean}
 */
function isInternalUrl(href) {
  try {
    return isInternalHost(new URL(href, window.location.origin).hostname);
  } catch {
    return true;
  }
}

/**
 * @param {string | null} rel
 * @param {string[]} tokensToRemove
 * @returns {string}
 */
function stripRelTokens(rel, tokensToRemove) {
  const remove = new Set(tokensToRemove);
  return (rel || "")
    .split(/\s+/)
    .filter((token) => token && token !== "" && !remove.has(token))
    .join(" ");
}

/**
 * @param {Element} anchor
 */
function applyLinkTarget(anchor) {
  if (!(anchor instanceof HTMLAnchorElement)) return;
  if (anchor.dataset.pcLinkNormalized === "true") return;
  if (anchor.hasAttribute("download")) return;
  // Theme-editor "open in new tab" checkboxes emit this from Liquid so
  // merchant-chosen _blank on an internal URL is preserved.
  if (anchor.dataset.pcLinkTarget === "skip") return;

  const href = anchor.getAttribute("href");
  if (href == null || shouldSkipHref(href)) return;

  if (isInternalUrl(href)) {
    if (anchor.target === "_blank" || anchor.target === "_new") {
      anchor.removeAttribute("target");
    }
    const nextRel = stripRelTokens(anchor.getAttribute("rel"), [
      "noopener",
      "noreferrer",
    ]);
    if (nextRel) {
      anchor.setAttribute("rel", nextRel);
    } else {
      anchor.removeAttribute("rel");
    }
  } else {
    anchor.target = "_blank";
    const rel = new Set(
      (anchor.getAttribute("rel") || "").split(/\s+/).filter(Boolean),
    );
    rel.add("noopener");
    rel.add("noreferrer");
    anchor.setAttribute("rel", [...rel].join(" "));
  }

  anchor.dataset.pcLinkNormalized = "true";
}

/**
 * @param {Element} root
 */
function applyToRoot(root) {
  if (root.matches("a[href]")) applyLinkTarget(root);
  root.querySelectorAll("a[href]").forEach(applyLinkTarget);
}

function init() {
  applyToRoot(document.documentElement);

  // childList covers newly injected markup. attributes/target covers in-place
  // morphs (section-renderer + morph.copyAttributes): server HTML restores
  // target="_blank" and drops data-pc-link-normalized without adding nodes.
  // Our own target writes are a no-op on the next delivery because the
  // dataset flag is set before the observer callback runs.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        applyLinkTarget(mutation.target);
        continue;
      }
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) applyToRoot(node);
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["target"],
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
